/**
 * GovTargetCheckPage — 「AI 판정 점검」 (2026-10-08)
 *  서버 셸에서 돌리던 두 점검을 화면에서 — 둘 다 공고 값을 바꾸지 않는다(시험 세트만 「대량 다시 읽기」 관문 기록을 남김)
 *   · 시험 세트   scripts/gov-targets-gold.json 의 지도사 점검 공고를 지금 질문 방식으로 읽고 기대값과 대조(✓ 맞음 · ✗ 틀림 · △ 참고 · ? 공고 없음) — 수집 공고 사례(caseCorpus)는 수집 공고 카드처럼 「원문 | AI」
 *   · 카드마다 AI 가 읽은 지역 · 지원형태 · 종류 · 정책자금 · 세부 유형을 표 아래 한 줄(meta)로 — 2026-10-08 수집 공고 50건 점검에서 이 값들이 화면에 없어 지역 제한 · 컨설팅형을 확인할 수 없었다
 *   · 무작위 표본 활성 공고 N건(시험 세트 제외)을 읽어 「지금 등록값 ↔ AI 답」을 칸마다 나란히 — 다른 칸은 파란 막대 · 굵게
 *   · 수집 공고   로움에 없는 수집 공고(scripts/gov-check-corpus.json)를 읽고 자동 규칙 검사(govTargetsLint)로 틀림 · 확인을 표시 — 카드는 「원문(지원대상) | AI」
 *  점검은 서버에서 뒤로 돈다(한 번에 하나) · 이 화면은 3초마다 진행을 본다 · 최근 5회 기록
 *  위 막대는 GovTopBar 와 같은 모양에 줄바꿈만 더했다(좁은 화면에서 탭 · 제목 · 시험 상태가 넘치지 않게)
 *
 *  브라우저 자동화용 표지 (바뀌지 않게 유지)
 *   [data-check-page]  data-run-id · data-run-status(running|done|error) · data-progress-done · data-progress-total · data-tv
 *   [data-action="run-gold" | "run-sample" | "run-corpus" | "copy-text" | "refresh"] · [data-input="sample-n" | "corpus-n"]
 *   [data-run-row="<id>"] data-kind · data-status · data-selected
 *   [data-filter="all|fail|diff|dropped|low|error"] (시험 세트 · 표본) · [data-filter="all|flag-fail|flag-warn|clean|dropped|error"] (수집 공고) · data-active
 *   [data-check-row="<번호>"] data-program-id(수집 공고는 수집 id) · data-kind · data-gold(ok|fail|soft|missing|error) · data-confidence · data-dropped(개수) · data-diff(다른 칸, 쉼표)
 *     수집 공고 카드만: data-flags(규칙 검사 k, 쉼표) · data-flag-level(fail|warn|clean · AI 읽기 실패면 error) · 시험 세트의 수집 사례: data-corpus-id(수집 id — 원문 | AI 로 그린다)
 *     [data-field="item|biz|subs|tech|bizx|sec|req|cond|amount|canon"] data-diff(1|0) > [data-side="now"|"ai"] (수집 공고는 "ai" 만)
 *     [data-part="bad" | "flags" | "target" | "meta" | "dropped" | "note" | "error" | "evidence"] · [data-flag="<k>"] data-level(fail|warn)
 *     [data-part="meta"] data-region · data-form(consult|빈) · data-kind · data-tracks(개수) > [data-meta="region|form|kind|loan|tracks"] — AI 가 읽은 지역 · 지원형태 · 종류 · 정책자금 · 세부 유형(2026-10-08 · 값이 하나도 없으면 없음)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api';
import { C, T, R, Badge } from '../components/ui';
import type {
  GovCheckInfo, GovCheckRun, GovCheckRunSummary, GovCheckItem, GovCheckCurrent, GovCheckAi, GovCheckAiTrack, GovCheckReq, GovCheckCond, GovCheckKind, GovAiConf, GovCheckFlag,
} from '../types';

/* ── 이 화면 값 ── */
const POLL_MS = 3000;
const FULL_MIN_MS = 6000;             // 진행 중 카드 전체를 다시 받는 최소 간격(진행 수는 3초마다)
const GOLD_HOURS = 48;                // 대량 다시 읽기 관문 — scripts/gov-targets-ai.js GOLD_HOURS
const KIND_LABEL: Record<GovCheckKind, string> = { gold: '시험 세트', sample: '무작위 표본', corpus: '수집 공고' };
const CORPUS_DEFAULT = 50;
const STATUS_LABEL: Record<string, string> = { running: '진행 중', done: '끝남', error: '오류' };
const CONF_LABEL: Record<GovAiConf, string> = { high: '높음', mid: '중간', low: '낮음' };
const AMBER = { bg: '#FFFBEB', line: '#FDE68A', text: '#92400E' };

type FieldKey = 'item' | 'biz' | 'subs' | 'tech' | 'bizx' | 'sec' | 'req' | 'cond' | 'amount' | 'canon';
const FIELDS: { k: FieldKey; label: string }[] = [
  { k: 'item', label: '칸' }, { k: 'biz', label: '업종' }, { k: 'subs', label: '세부업종' }, { k: 'tech', label: '기술 기반' },
  { k: 'bizx', label: '제외 업종' }, { k: 'sec', label: '전용 대상' }, { k: 'req', label: '자격' }, { k: 'cond', label: '숫자 조건' },
  { k: 'amount', label: '금액' }, { k: 'canon', label: '정식 사업명' },
];
type Filter = 'all' | 'fail' | 'diff' | 'dropped' | 'low' | 'error' | 'flag-fail' | 'flag-warn' | 'clean';

/* ── 시각(KST) ── */
const kst = (iso: string) => new Date(new Date(iso).getTime() + 9 * 3600 * 1000).toISOString();
function when(iso: string | null | undefined): string {
  if (!iso || Number.isNaN(Date.parse(iso))) return '';
  const d = kst(iso), today = kst(new Date().toISOString()).slice(0, 10);
  return d.slice(0, 10) === today ? `오늘 ${d.slice(11, 16)}` : `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))} ${d.slice(11, 16)}`;
}
const won = (n: number) => `${Math.round(n).toLocaleString('ko-KR')}원`;

/* ── 값 → 글 ── */
const uniqSorted = (a: string[] | null | undefined) => Array.from(new Set(a || [])).sort();
const sameSet = (a: string[] | null | undefined, b: string[] | null | undefined) => { const x = uniqSorted(a), y = uniqSorted(b); return x.length === y.length && x.every((v, i) => v === y[i]); };
function condText(c: GovCheckCond | null | undefined): string {
  if (!c) return '';
  const o: string[] = [];
  if (c.emp) o.push(`근로자 ${c.emp.op}${c.emp.v}명`);
  if (c.sales) o.push(`매출 ${c.sales.op}${c.sales.v}억`);
  if (c.yrs && (c.yrs.min != null || c.yrs.max != null)) o.push(`업력 ${c.yrs.min != null ? c.yrs.min : 0}~${c.yrs.max != null ? c.yrs.max : ''}년`);
  if (c.scale) o.push(`규모 ${c.scale === '중견기업' ? '중소·중견기업' : c.scale}`);   // 상한 — 중소기업도 통과
  if (c.age && (c.age.min != null || c.age.max != null)) o.push(`대표 만 ${c.age.min != null ? c.age.min : ''}~${c.age.max != null ? c.age.max : ''}세`);
  return o.join(' · ');
}
const squash = (s: string | null | undefined) => String(s || '').replace(/\s+/g, '');
const reqKeys = (a: GovCheckReq[]) => a.map(q => `${q.t}|${q.k}`);
/** 다른 칸 — backend services/govTargetsCheck.js diffFields 와 같은 규칙 */
function diffFields(cur: GovCheckCurrent | null, ai: GovCheckAi | null): FieldKey[] {
  if (!cur || !ai) return [];
  const d: FieldKey[] = [];
  if ((ai.item || '') !== (cur.item || '')) d.push('item');
  if (!sameSet(ai.biz, cur.biz)) d.push('biz');
  if (!sameSet(ai.subs, cur.subs)) d.push('subs');
  if (!!ai.tech !== !!cur.tech) d.push('tech');
  if (!sameSet(ai.bizx, cur.bizx)) d.push('bizx');
  if (!sameSet(ai.sec, cur.sec)) d.push('sec');
  if (!sameSet(reqKeys(ai.req || []), reqKeys(cur.reqItems || []))) d.push('req');
  if (condText(ai.cond) !== condText(cur.cond)) d.push('cond');
  const aMax = ai.amount ? ai.amount.max : null;
  if ((ai.amount ? squash(ai.amount.text) : '') !== squash(cur.amount) || (aMax != null && aMax !== cur.amountMax)) d.push('amount');
  if ((ai.canon || '') !== (cur.canon || '')) d.push('canon');
  return d;
}
type GoldMark = 'ok' | 'fail' | 'soft' | 'missing' | 'error';
function goldMark(it: GovCheckItem): GoldMark {
  if (it.missing) return 'missing';
  if (it.err || !it.ai) return 'error';
  return it.ok ? 'ok' : it.soft ? 'soft' : 'fail';
}
/** 수집 공고 — 규칙 검사 결과로 ✗(틀림 하나라도) · △(확인만) · ✓(없음) · 실패 */
type FlagLevel = 'fail' | 'warn' | 'clean';
const flagsOf = (it: GovCheckItem): GovCheckFlag[] => (Array.isArray(it.flags) ? it.flags : []);
function flagLevel(it: GovCheckItem): FlagLevel {
  const f = flagsOf(it);
  return f.some(x => x.level === 'fail') ? 'fail' : f.length ? 'warn' : 'clean';
}
function corpusMark(it: GovCheckItem): GoldMark {
  if (it.err || !it.ai) return 'error';
  const lv = flagLevel(it);
  return lv === 'fail' ? 'fail' : lv === 'warn' ? 'soft' : 'ok';
}
const MARK: Record<GoldMark, { ch: string; color: string; bg: string; label: string }> = {
  ok: { ch: '✓', color: C.green, bg: C.greenLt, label: '맞음' },
  fail: { ch: '✗', color: C.red, bg: C.redLt, label: '틀림' },
  soft: { ch: '△', color: AMBER.text, bg: AMBER.bg, label: '참고' },
  missing: { ch: '?', color: C.sub, bg: C.bg, label: '공고 없음' },
  error: { ch: '✗', color: C.red, bg: C.redLt, label: '실패' },
};
const markLabel = (kind: GovCheckKind, m: GoldMark) => (kind === 'corpus' ? ({ ok: '깨끗', fail: '틀림', soft: '확인', missing: '없음', error: '실패' } as Record<GoldMark, string>)[m] : MARK[m].label);
const CONF_COLOR: Record<GovAiConf, [string, string]> = { high: [C.greenLt, C.green], mid: [C.bg, C.sub], low: [AMBER.bg, AMBER.text] };

async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* 아래 대체 */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
  } catch { return false; }
}
function useNarrow(px: number) {
  const [n, setN] = useState(() => typeof window !== 'undefined' && window.innerWidth < px);
  useEffect(() => { const f = () => setN(window.innerWidth < px); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, [px]);
  return n;
}
const errMsg = (e: unknown) => (e instanceof Error ? e.message : '요청에 실패했습니다');

/* ═══ 화면 ═══ */
export default function GovTargetCheckPage({ topLeft }: { topLeft: React.ReactNode }) {
  const narrow = useNarrow(980);
  const compact = useNarrow(720);
  const [info, setInfo] = useState<GovCheckInfo | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [run, setRun] = useState<GovCheckRun | null>(null);
  const [runErr, setRunErr] = useState('');
  const [nText, setNText] = useState('');
  const [cText, setCText] = useState(String(CORPUS_DEFAULT));
  const [starting, setStarting] = useState<GovCheckKind | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; msg: string } | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [exporting, setExporting] = useState(false);
  // 검수용 내려받기 (2026-10-09 지도사 — 병렬 수기 검수) · 활성 공고 전부라 1~2분 걸릴 수 있다
  const onExport = useCallback(async () => {
    setExporting(true); setNotice(null);
    try { const r = await api.downloadGovReviewExport(); setNotice({ tone: 'ok', msg: `검수용 파일을 받았습니다 — 공고 ${r.count.toLocaleString()}건 · ${r.filename}` }); }
    catch (e) { setNotice({ tone: 'err', msg: e instanceof Error ? e.message : '내려받기 실패' }); }
    finally { setExporting(false); }
  }, []);
  const lastFull = useRef<{ id: string; done: number; at: number; status: string } | null>(null);
  const selRef = useRef<string | null>(null);   // 늦게 온 다른 작업의 응답이 지금 고른 작업을 덮지 않게
  selRef.current = selId;

  const loadInfo = useCallback(async () => {
    try { const r = (await api.getGovCheckInfo()).data; setInfo(r); setLoadErr(''); return r; }
    catch (e) { setLoadErr(errMsg(e)); return null; }
  }, []);
  const loadRun = useCallback(async (id: string) => {
    try {
      const r = (await api.getGovCheckRun(id)).data;
      if (selRef.current !== id) return;
      setRun(r);
      setRunErr('');
      lastFull.current = { id, done: r.progress ? r.progress.done : 0, at: Date.now(), status: r.status };
    } catch (e) { if (selRef.current === id) setRunErr(errMsg(e)); }
  }, []);

  // 처음 — 목록 · 가장 최근 작업을 연다
  useEffect(() => {
    void loadInfo().then(r => {
      if (!r) return;
      setNText(t => t || String(r.nDefault));
      const cm = Math.min(r.corpusMax || 0, r.corpusCount || 0);
      if (cm > 0 && cm < CORPUS_DEFAULT) setCText(String(cm));   // 수집 공고가 50건보다 적으면 있는 만큼
      const first = r.active || (r.runs[0] && r.runs[0].id) || null;
      if (first) setSelId(s => s || first);
    });
  }, [loadInfo]);
  // 고른 작업 열기
  useEffect(() => {
    if (!selId) { setRun(null); return; }
    setRun(cur => (cur && cur.id === selId ? cur : null));
    setRunErr('');
    setFilter('all');
    void loadRun(selId);
  }, [selId, loadRun]);

  // 진행 중이면 3초마다 — 진행 수는 매번 · 카드 전체는 수가 바뀌고 6초 지났을 때(또는 끝났을 때)
  const activeId = info ? info.active : null;
  const selSummary = info && selId ? info.runs.find(r => r.id === selId) || null : null;
  const polling = !!activeId || (selSummary ? selSummary.status === 'running' : false);
  useEffect(() => {
    if (!polling) return undefined;
    let stop = false;
    const tick = async () => {
      const r = await loadInfo();
      if (stop || !r || !selId) return;
      const s = r.runs.find(x => x.id === selId);
      if (!s) return;
      const lf = lastFull.current;
      const changed = !lf || lf.id !== s.id || lf.done !== s.progress.done || lf.status !== s.status;
      if (changed && (s.status !== 'running' || !lf || Date.now() - lf.at >= FULL_MIN_MS)) void loadRun(s.id);
    };
    const h = window.setInterval(() => { void tick(); }, POLL_MS);
    return () => { stop = true; window.clearInterval(h); };
  }, [polling, selId, loadInfo, loadRun]);

  const nMax = info ? info.nMax : 200;
  const perItem = info ? info.perItemKrw : 150;
  const nVal = Math.round(Number(nText));
  const nOk = Number.isFinite(nVal) && nVal >= 1 && nVal <= nMax;
  const busy = !!activeId || !!starting;
  const corpusCount = info ? info.corpusCount || 0 : 0;
  const cMax = info ? Math.min(info.corpusMax || 0, corpusCount) : 0;
  const cVal = Math.round(Number(cText));
  const cOk = cMax > 0 && Number.isFinite(cVal) && cVal >= 1 && cVal <= cMax;

  async function start(kind: GovCheckKind) {
    if (!info || busy) return;
    const count = kind === 'gold' ? info.goldCases : kind === 'corpus' ? cVal : nVal;
    if (kind === 'sample' && !nOk) { setNotice({ tone: 'err', msg: `건수는 1~${nMax} 사이로 넣으세요` }); return; }
    if (kind === 'corpus' && !cOk) { setNotice({ tone: 'err', msg: cMax ? `건수는 1~${cMax} 사이로 넣으세요` : '수집 공고가 없습니다' }); return; }
    const what = kind === 'gold' ? `시험 세트 ${count}건` : kind === 'corpus' ? `수집 공고 ${count}건` : `무작위 표본 ${count}건`;
    const left = info.budget.remainingKrw;
    const ok = window.confirm(`${what}을 AI 로 읽습니다.\n\n예상 비용 약 ${won(count * perItem)} (건당 약 ${won(perItem)} · Opus 직접 호출)${left != null ? `\n남은 AI 예산 ${won(left)}` : ''}\n\n공고 값은 바꾸지 않습니다${kind === 'gold' ? ' — 결과는 대량 다시 읽기 관문 기록으로만 남습니다' : kind === 'corpus' ? ' — 로움에 없는 수집 공고라 저장할 곳도 없습니다' : ''}.`);
    if (!ok) return;
    setStarting(kind); setNotice(null);
    try {
      const r = (await api.startGovCheck(kind === 'gold' ? { kind } : { kind, n: count })).data;
      lastFull.current = null;
      setSelId(r.id);
      await loadInfo();
      setNotice({ tone: 'ok', msg: `${what} 점검을 시작했습니다 — 화면을 닫아도 서버에서 계속 돕니다` });
    } catch (e) { setNotice({ tone: 'err', msg: errMsg(e) }); void loadInfo(); }
    finally { setStarting(null); }
  }

  async function copyRun() {
    if (!run) return;
    const ok = await copyText(run.text || '');
    setNotice(ok ? { tone: 'ok', msg: `${KIND_LABEL[run.kind]} 결과를 글로 복사했습니다 (${(run.text || '').split('\n').length.toLocaleString()}줄)` } : { tone: 'err', msg: '복사하지 못했습니다 — 브라우저 권한을 확인하세요' });
  }

  /* ── 카드 · 거르기 ── */
  const rows = useMemo(() => (run ? run.items.map(it => ({ it, diff: diffFields(it.current, it.ai), mark: run.kind === 'gold' ? goldMark(it) : run.kind === 'corpus' ? corpusMark(it) : null, level: flagLevel(it) })) : []), [run]);
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: rows.length, fail: 0, diff: 0, dropped: 0, low: 0, error: 0, 'flag-fail': 0, 'flag-warn': 0, clean: 0 };
    for (const r of rows) {
      const ok = !!r.it.ai && !r.it.err;
      if (ok && r.level === 'fail') c['flag-fail'] += 1;
      if (ok && r.level === 'warn') c['flag-warn'] += 1;
      if (ok && r.level === 'clean') c.clean += 1;
      if (r.mark === 'fail' || r.mark === 'soft' || r.mark === 'error') c.fail += 1;
      if (r.diff.length) c.diff += 1;
      if (r.it.ai && r.it.ai.dropped.length) c.dropped += 1;
      if (r.it.ai && r.it.ai.confidence === 'low') c.low += 1;
      if (!r.it.missing && (r.it.err || !r.it.ai)) c.error += 1;
    }
    return c;
  }, [rows]);
  const shown = useMemo(() => rows.filter(r => {
    if (filter === 'fail') return r.mark === 'fail' || r.mark === 'soft' || r.mark === 'error';
    if (filter === 'diff') return r.diff.length > 0;
    if (filter === 'dropped') return !!(r.it.ai && r.it.ai.dropped.length);
    if (filter === 'low') return !!(r.it.ai && r.it.ai.confidence === 'low');
    if (filter === 'error') return !r.it.missing && (!!r.it.err || !r.it.ai);
    if (filter === 'flag-fail' || filter === 'flag-warn' || filter === 'clean') return !!r.it.ai && !r.it.err && r.level === (filter === 'flag-fail' ? 'fail' : filter === 'flag-warn' ? 'warn' : 'clean');
    return true;
  }), [rows, filter]);

  /* ── 위 막대 · 시험 세트 상태 ── */
  const gs = info ? info.goldState : null;
  const goldFresh = !!(gs && info && gs.tv === info.tv && Date.now() - Date.parse(gs.at) < GOLD_HOURS * 3600 * 1000);
  const goldTone = !gs || !info ? 'none' : gs.tv !== info.tv ? 'old' : gs.fail ? 'fail' : goldFresh ? 'pass' : 'expired';
  const goldText = !gs || !info ? '시험 기록 없음'
    : gs.tv !== info.tv ? `지금 질문 방식(${info.tv})으로 아직 시험 안 함 · 마지막 ${gs.tv} ${when(gs.at)}`
      : gs.fail ? `틀림 ${gs.fail}건 · ${when(gs.at)}` : `통과${gs.soft ? ` (참고 ${gs.soft})` : ''} · ${when(gs.at)}${goldFresh ? '' : ` · ${GOLD_HOURS}시간 지남`}`;
  const goldColor = goldTone === 'pass' ? [C.greenLt, C.green] : goldTone === 'fail' ? [C.redLt, C.red] : [AMBER.bg, AMBER.text];

  const activeSummary = info && activeId ? info.runs.find(r => r.id === activeId) || null : null;

  return (
    <div data-check-page="" data-run-id={run ? run.id : ''} data-run-status={run ? run.status : ''}
      data-progress-done={run ? run.progress.done : 0} data-progress-total={run ? run.progress.total : 0} data-tv={info ? info.tv : ''}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, background: C.white }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', minHeight: 50, boxSizing: 'border-box', borderBottom: `1px solid ${C.border}`, flexShrink: 0, minWidth: 0, flexWrap: 'wrap' }}>
        <div style={{ maxWidth: '100%', overflowX: 'auto', flexShrink: 0 }}>{topLeft}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: T.lg, fontWeight: 700, color: C.text, marginLeft: 6, whiteSpace: 'nowrap' }}>AI 판정 점검</span>
          {info && <Badge bg={C.bg} color={C.sub} style={{ border: `1px solid ${C.border}` }}>질문 방식 <b style={{ color: C.text }}>{info.tv}</b></Badge>}
          {info && (
            <span data-gold-state={goldTone} title="대량 다시 읽기(--all · --unanswered 등 --apply)는 지금 질문 방식으로 48시간 안에 틀림 0 으로 통과한 기록이 있어야 시작합니다"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: R.sm, background: goldColor[0], color: goldColor[1], fontSize: T.xs, fontWeight: 600, whiteSpace: 'nowrap' }}>
              마지막 시험 세트 · {goldText}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button type="button" data-action="export-review" disabled={exporting} onClick={() => { void onExport(); }}
            title="활성 공고 전부의 지금 판정과 AI 가 읽는 본문을 파일 하나(jsonl.gz)로 받습니다 — 수기 검수용 · 1~2분 걸릴 수 있습니다"
            style={{ ...btnGhost, opacity: exporting ? 0.6 : 1, cursor: exporting ? 'wait' : 'pointer' }}>{exporting ? '만드는 중…' : '검수용 내려받기'}</button>
          <button type="button" data-action="refresh" onClick={() => { void loadInfo(); if (selId) void loadRun(selId); }}
            style={btnGhost}>새로고침</button>
        </div>
      </div>

      {loadErr && !info && <div style={{ padding: 16, color: C.red, fontSize: T.md }}>불러오지 못했습니다 — {loadErr}</div>}

      <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', flex: 1, minHeight: 0, overflowY: narrow ? 'auto' : 'hidden' }}>
        {/* ═══ 왼쪽: 돌리기 · 최근 점검 ═══ */}
        <aside style={{ width: narrow ? 'auto' : 300, flexShrink: 0, borderRight: narrow ? 'none' : `1px solid ${C.border}`, borderBottom: narrow ? `1px solid ${C.border}` : 'none', overflowY: narrow ? 'visible' : 'auto', padding: '14px 16px', boxSizing: 'border-box', background: C.bgAlt }}>
          <div style={secTitle}>점검 돌리기</div>
          {info && !info.aiConfigured && <div style={{ ...noticeBox('err'), marginBottom: 10 }}>서버에 ANTHROPIC_API_KEY 가 없어 점검을 돌릴 수 없습니다</div>}

          <div style={actionCard}>
            <div style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>시험 세트</div>
            <div style={hintText}>지도사가 점검한 공고 {info ? info.goldCases : '…'}건을 지금 질문 방식으로 읽고 기대값과 대조합니다. 결과는 대량 다시 읽기 관문으로만 남습니다.</div>
            <button type="button" data-action="run-gold" disabled={!info || busy || !info.aiConfigured} onClick={() => { void start('gold'); }}
              style={btnPrimary(!info || busy || !info.aiConfigured)}>
              {starting === 'gold' ? '시작하는 중…' : `시험 세트 돌리기 (${info ? info.goldCases : '…'}건)`}
            </button>
            <div style={costText}>약 {won((info ? info.goldCases : 0) * perItem)} · 건당 약 {won(perItem)} (Opus 직접 호출)</div>
          </div>

          <div style={{ ...actionCard, marginTop: 10 }}>
            <div style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>무작위 표본</div>
            <div style={hintText}>활성 공고를 무작위로 골라(시험 세트 제외) 지금 등록값과 AI 답을 칸마다 나란히 봅니다. 저장하지 않습니다.</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input data-input="sample-n" type="number" inputMode="numeric" min={1} max={nMax} step={1} value={nText} aria-label="표본 건수"
                onChange={e => setNText(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
                onBlur={() => { if (nText && nOk) setNText(String(nVal)); }}
                style={{ width: 72, padding: '7px 8px', borderRadius: R.sm, border: `1px solid ${nText && !nOk ? C.red : C.borderDk}`, fontSize: T.md, fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box', background: C.white }} />
              <span style={{ fontSize: T.sm, color: C.sub }}>건 (최대 {nMax})</span>
            </div>
            <button type="button" data-action="run-sample" disabled={!info || busy || !nOk || !info.aiConfigured} onClick={() => { void start('sample'); }}
              style={btnPrimary(!info || busy || !nOk || !info.aiConfigured)}>
              {starting === 'sample' ? '시작하는 중…' : '무작위 표본 돌리기'}
            </button>
            <div style={costText}>{nOk ? `약 ${won(nVal * perItem)}` : `1~${nMax}건`} · 건당 약 {won(perItem)} (Opus 직접 호출)</div>
          </div>

          <div style={{ ...actionCard, marginTop: 10 }}>
            <div style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>수집 공고 시험</div>
            <div style={hintText}>로움에 등록되지 않은 수집 공고{corpusCount ? ` ${corpusCount.toLocaleString()}건` : ''}에서 골라 AI 로 읽고, 자동 규칙 검사로 틀림 · 확인을 표시합니다. 저장하지 않습니다.</div>
            {info && !corpusCount ? (
              <div data-part="corpus-empty" style={{ ...noticeBox('warn'), fontSize: T.sm }}>수집 공고 파일이 아직 없습니다 — 서버에 scripts/gov-check-corpus.json 이 올라오면 돌릴 수 있습니다</div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input data-input="corpus-n" type="number" inputMode="numeric" min={1} max={cMax || undefined} step={1} value={cText} aria-label="수집 공고 건수" disabled={!cMax}
                  onChange={e => setCText(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
                  onBlur={() => { if (cText && cOk) setCText(String(cVal)); }}
                  style={{ width: 72, padding: '7px 8px', borderRadius: R.sm, border: `1px solid ${cText && cMax && !cOk ? C.red : C.borderDk}`, fontSize: T.md, fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box', background: C.white }} />
                <span style={{ fontSize: T.sm, color: C.sub }}>건 (최대 {cMax.toLocaleString()})</span>
              </div>
            )}
            <button type="button" data-action="run-corpus" disabled={!info || busy || !cOk || !info.aiConfigured} onClick={() => { void start('corpus'); }}
              style={btnPrimary(!info || busy || !cOk || !info.aiConfigured)}>
              {starting === 'corpus' ? '시작하는 중…' : '수집 공고 시험 돌리기'}
            </button>
            {!!cMax && <div style={costText}>{cOk ? `약 ${won(cVal * perItem)}` : `1~${cMax}건`} · 건당 약 {won(perItem)} (Opus 직접 호출)</div>}
          </div>

          {info && info.budget.remainingKrw != null && <div style={{ ...costText, marginTop: 8 }}>남은 AI 예산 {won(info.budget.remainingKrw)}</div>}
          {activeSummary && (
            <div data-part="active" style={{ marginTop: 12, padding: '10px 12px', borderRadius: R.md, background: C.accentLt, border: `1px solid #BFDBFE` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.sm, fontWeight: 700, color: C.accent }}>
                <span>{KIND_LABEL[activeSummary.kind]} 진행 중</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{activeSummary.progress.done} / {activeSummary.progress.total}건</span>
              </div>
              <ProgressBar done={activeSummary.progress.done} total={activeSummary.progress.total} />
              <div style={{ ...costText, marginTop: 6 }}>{when(activeSummary.startedAt)} 시작 · 끝날 때까지 다른 점검은 시작할 수 없습니다</div>
            </div>
          )}
          {notice && <div role="status" style={{ ...noticeBox(notice.tone), marginTop: 10 }}>{notice.msg}</div>}

          <div style={{ ...secTitle, marginTop: 20 }}>최근 점검 <span style={{ color: C.muted, fontWeight: 500 }}>(5회)</span></div>
          {info && !info.runs.length && <div style={hintText}>아직 점검 기록이 없습니다.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {info && info.runs.map(r => <RunRow key={r.id} r={r} selected={r.id === selId} onClick={() => setSelId(r.id)} />)}
          </div>
        </aside>

        {/* ═══ 오른쪽: 결과 ═══ */}
        <main style={{ flex: 1, minWidth: 0, overflowY: narrow ? 'visible' : 'auto', padding: compact ? '14px 12px 40px' : '16px 20px 48px', boxSizing: 'border-box' }}>
          {!selId && info && <Empty text="왼쪽에서 점검을 돌리거나 최근 점검을 고르세요." />}
          {selId && !run && !runErr && <Empty text="불러오는 중…" />}
          {runErr && <div style={noticeBox('err')}>{runErr}</div>}
          {run && (
            <>
              <RunHeader run={run} onCopy={() => { void copyRun(); }} />
              {run.status === 'running' && (
                <div style={{ margin: '10px 0 4px' }}><ProgressBar done={run.progress.done} total={run.progress.total} /></div>
              )}
              {run.status === 'error' && run.error && <div data-part="run-error" style={{ ...noticeBox('err'), marginTop: 10 }}>점검이 멈췄습니다 — {run.error}</div>}

              <div role="tablist" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '14px 0 12px' }}>
                {(run.kind === 'corpus'
                  ? [['all', '전체'], ['flag-fail', '틀림'], ['flag-warn', '확인'], ['clean', '깨끗'], ['dropped', '버린 값'], ['error', '실패']] as [Filter, string][]
                  : [
                    ['all', '전체'],
                    ...(run.kind === 'gold' ? [['fail', '틀림만']] : []),
                    ['diff', '지금과 다름'], ['dropped', '버린 값 있음'], ['low', '확신 낮음'], ['error', '실패'],
                  ] as [Filter, string][]).filter(([k]) => k === 'all' || k === 'fail' || (run.kind === 'corpus' && k !== 'error' && k !== 'dropped') || counts[k] > 0 || filter === k).map(([k, label]) => {
                  const act = filter === k;
                  return (
                    <button key={k} type="button" role="tab" aria-selected={act} data-filter={k} data-active={act ? '1' : '0'} onClick={() => setFilter(k)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, cursor: 'pointer', fontSize: T.sm, fontWeight: act ? 700 : 500,
                        border: `1px solid ${act ? C.accent : C.border}`, background: act ? C.accentLt : C.white, color: act ? C.accent : C.sub }}>
                      {label}<span style={{ fontVariantNumeric: 'tabular-nums', color: act ? C.accent : (k === 'fail' || k === 'flag-fail') && counts[k] ? C.red : k === 'flag-warn' && counts[k] ? AMBER.text : C.muted, fontWeight: 700 }}>{counts[k]}</span>
                    </button>
                  );
                })}
              </div>

              {!shown.length && <Empty text={run.status === 'running' && !rows.length ? '첫 결과를 기다리는 중…' : '이 조건에 맞는 공고가 없습니다.'} />}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {shown.map(r => <CheckCard key={`${run.id}-${r.it.n}`} it={r.it} kind={run.kind} diff={r.diff} mark={r.mark} compact={compact} />)}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

/* ── 조각 ── */
const secTitle: React.CSSProperties = { fontSize: T.sm, fontWeight: 600, color: C.sub, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.border}` };
const hintText: React.CSSProperties = { fontSize: T.sm, color: C.sub, lineHeight: 1.55 };
const costText: React.CSSProperties = { fontSize: T.xs, color: C.sub, fontVariantNumeric: 'tabular-nums' };
const actionCard: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 12px 10px', borderRadius: R.md, background: C.white, border: `1px solid ${C.border}` };
const btnGhost: React.CSSProperties = { padding: '6px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 };
const btnPrimary = (off: boolean): React.CSSProperties => ({
  padding: '9px 12px', borderRadius: R.sm, border: 'none', cursor: off ? 'not-allowed' : 'pointer', fontSize: T.md, fontWeight: 700,
  background: off ? C.border : C.text, color: off ? C.muted : C.white,
});
const noticeBox = (tone: 'ok' | 'err' | 'warn'): React.CSSProperties => ({
  padding: '8px 10px', borderRadius: R.sm, fontSize: T.sm, lineHeight: 1.5,
  background: tone === 'ok' ? C.greenLt : tone === 'warn' ? AMBER.bg : C.redLt, color: tone === 'ok' ? '#166534' : tone === 'warn' ? AMBER.text : '#991B1B',
  border: `1px solid ${tone === 'ok' ? '#BBF7D0' : tone === 'warn' ? AMBER.line : '#FECACA'}`,
});

function Empty({ text }: { text: string }) {
  return <div style={{ padding: '40px 0', textAlign: 'center', fontSize: T.md, color: C.muted }}>{text}</div>;
}
function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} style={{ height: 6, borderRadius: 999, background: '#DBEAFE', overflow: 'hidden', marginTop: 6 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: C.accent, transition: 'width 0.4s' }} />
    </div>
  );
}

function RunRow({ r, selected, onClick }: { r: GovCheckRunSummary; selected: boolean; onClick: () => void }) {
  const s = r.summary;
  const tone = r.status === 'running' ? C.accent : r.status === 'error' ? C.red : (r.kind === 'gold' || r.kind === 'corpus') && s && s.fail ? C.red : r.kind === 'corpus' && s && s.warn ? AMBER.text : C.green;
  const result = r.status === 'running' ? `${r.progress.done}/${r.progress.total}건`
    : r.status === 'error' ? '오류'
      : r.kind === 'gold' ? (s && s.fail ? `틀림 ${s.fail}` : '통과') + (s && s.soft ? ` · 참고 ${s.soft}` : '')
      : r.kind === 'corpus' ? `틀림 ${s && s.fail ? s.fail : 0} · 확인 ${s && s.warn ? s.warn : 0}`
        : `읽음 ${s && s.read != null ? s.read : r.itemCount}${s && s.errors ? ` · 실패 ${s.errors}` : ''}`;
  return (
    <button type="button" data-run-row={r.id} data-kind={r.kind} data-status={r.status} data-selected={selected ? '1' : '0'} onClick={onClick}
      style={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left', width: '100%', padding: '9px 11px', borderRadius: R.md, cursor: 'pointer',
        border: `1px solid ${selected ? C.accent : C.border}`, background: selected ? C.accentLt : C.white }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>{KIND_LABEL[r.kind]} <span style={{ fontWeight: 500, color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{r.n}건</span></span>
        <span style={{ fontSize: T.xs, fontWeight: 700, color: tone, whiteSpace: 'nowrap' }}>{result}</span>
      </span>
      <span style={{ fontSize: T.xs, color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{when(r.startedAt)} · {r.tv} · {STATUS_LABEL[r.status] || r.status}</span>
    </button>
  );
}

function RunHeader({ run, onCopy }: { run: GovCheckRun; onCopy: () => void }) {
  const s = run.summary;
  const u = run.usage;
  const chips: { label: string; n: number; color: string }[] = [];
  if (s && run.kind === 'gold') {
    chips.push({ label: '시험', n: s.total, color: C.text }, { label: '틀림', n: s.fail || 0, color: s.fail ? C.red : C.sub }, { label: '참고', n: s.soft || 0, color: s.soft ? AMBER.text : C.sub });
    if (s.missing) chips.push({ label: '공고 없음', n: s.missing, color: C.sub });
  } else if (s && run.kind === 'corpus') {
    chips.push({ label: '읽음', n: s.read || 0, color: C.text }, { label: '틀림', n: s.fail || 0, color: s.fail ? C.red : C.sub }, { label: '확인', n: s.warn || 0, color: s.warn ? AMBER.text : C.sub },
      { label: '깨끗', n: s.clean || 0, color: s.clean ? C.green : C.sub });
    if (s.errors) chips.push({ label: '실패', n: s.errors, color: C.red });
    if (s.notSent) chips.push({ label: '예산 밖', n: s.notSent, color: C.red });
  } else if (s) {
    chips.push({ label: '읽음', n: s.read || 0, color: C.text }, { label: '지금과 다름', n: s.diff || 0, color: s.diff ? C.accent : C.sub }, { label: '버린 값', n: s.dropped || 0, color: s.dropped ? AMBER.text : C.sub },
      { label: '확신 낮음', n: s.low || 0, color: s.low ? AMBER.text : C.sub });
    if (s.errors) chips.push({ label: '실패', n: s.errors, color: C.red });
    if (s.notSent) chips.push({ label: '예산 밖', n: s.notSent, color: C.red });
  }
  const verdict = run.kind === 'gold' && run.status === 'done' && s ? (s.fail ? { t: '통과 못 함 — 대량 다시 읽기를 막습니다', c: C.red, bg: C.redLt } : { t: '통과 — 대량 다시 읽기를 돌려도 됩니다(48시간 안)', c: C.green, bg: C.greenLt }) : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.lg, fontWeight: 700, color: C.text }}>{KIND_LABEL[run.kind]} {run.progress.total || run.n}건</span>
            <Badge bg={run.status === 'running' ? C.accentLt : run.status === 'error' ? C.redLt : C.bg} color={run.status === 'running' ? C.accent : run.status === 'error' ? C.red : C.sub}>
              {STATUS_LABEL[run.status] || run.status}{run.status === 'running' ? ` ${run.progress.done}/${run.progress.total}` : ''}
            </Badge>
          </div>
          <div style={{ fontSize: T.sm, color: C.sub, marginTop: 4, lineHeight: 1.5 }}>
            {when(run.startedAt)} 시작{run.finishedAt ? ` · ${when(run.finishedAt)} 끝` : ''} · 질문 방식 {run.tv} · 모델 {run.model}{run.by ? ` · ${run.by}` : ''}
            {u && u.calls ? ` · 호출 ${u.calls.toLocaleString()} · 토큰 입력 ${Math.round(u.inTok / 1000).toLocaleString()}k 출력 ${Math.round(u.outTok / 1000).toLocaleString()}k · 약 $${u.usd.toFixed(2)}` : ''}
          </div>
        </div>
        <button type="button" data-action="copy-text" onClick={onCopy} disabled={!run.text} style={{ ...btnGhost, color: C.text }}>텍스트로 복사</button>
      </div>
      {(chips.length > 0 || verdict) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {chips.map(c => (
            <span key={c.label} data-summary={c.label} style={{ fontSize: T.sm, color: C.sub, whiteSpace: 'nowrap' }}>
              {c.label} <b style={{ fontSize: T.md, color: c.color, fontVariantNumeric: 'tabular-nums' }}>{c.n.toLocaleString()}</b>
            </span>
          ))}
          {verdict && <span data-part="verdict" style={{ padding: '3px 9px', borderRadius: R.sm, background: verdict.bg, color: verdict.c, fontSize: T.sm, fontWeight: 700 }}>{verdict.t}</span>}
        </div>
      )}
    </div>
  );
}

/* ── 카드 한 장 ── */
const Dash = () => <span style={{ color: C.muted }}>없음</span>;
function Chips({ items, tone }: { items: string[]; tone?: 'ai' }) {
  if (!items.length) return <Dash />;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {items.map((x, i) => (
        <span key={`${x}-${i}`} style={{ padding: '1px 7px', borderRadius: 5, fontSize: T.sm, lineHeight: 1.6, background: tone === 'ai' ? C.white : C.bg, border: `1px solid ${C.border}`, color: C.text }}>{x}</span>
      ))}
    </span>
  );
}
function ReqList({ list }: { list: GovCheckReq[] }) {
  if (!list.length) return <Dash />;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {list.map((q, i) => (
        <span key={`${q.t}-${i}`} title={q.ev || undefined} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 5, fontSize: T.sm, lineHeight: 1.6, border: `1px solid ${q.k === 'gain' ? '#DDD6FE' : C.border}`, background: q.k === 'gain' ? C.purpleLt : C.white, color: C.text }}>
          {q.t}
          <span style={{ fontSize: T.xs, fontWeight: 700, color: q.k === 'gain' ? C.purple : C.sub }}>{q.k === 'gain' ? '신청 전' : '보유'}</span>
          {q.f.length > 0 && <span style={{ fontSize: T.xs, color: C.accent, fontWeight: 600 }}>→ {q.f.join(' / ')}</span>}
        </span>
      ))}
    </span>
  );
}
function amountNow(cur: GovCheckCurrent): string {
  return [cur.amount, cur.amountMax != null ? `최대 ${cur.amountMax.toLocaleString()}만원` : ''].filter(Boolean).join(' · ');
}
function amountAi(ai: GovCheckAi): string {
  const a = ai.amount;
  if (!a) return '';
  return [`${a.per ? '1인당 ' : ''}${a.text || ''}`.trim(), a.max != null ? `최대 ${a.max.toLocaleString()}만원` : ''].filter(Boolean).join(' · ');
}
/** 칸 하나의 지금 · AI 값 (비었는지 · 그릴 것) */
function cellOf(k: FieldKey, cur: GovCheckCurrent | null, ai: GovCheckAi | null, side: 'now' | 'ai'): { empty: boolean; node: React.ReactNode } {
  const txt = (s: string) => ({ empty: !s, node: s ? <span>{s}</span> : <Dash /> });
  if (side === 'now') {
    if (!cur) return { empty: true, node: <Dash /> };
    switch (k) {
      case 'item': return txt(cur.category);
      case 'biz': return { empty: !cur.biz.length, node: <Chips items={cur.biz} /> };
      case 'subs': return { empty: !cur.subs.length, node: <Chips items={cur.subs} /> };
      case 'tech': return txt(cur.tech ? '켬' : '');
      case 'bizx': return { empty: !cur.bizx.length, node: <Chips items={cur.bizx} /> };
      case 'sec': return { empty: !cur.sec.length, node: <Chips items={cur.sec} /> };
      case 'req': return { empty: !cur.reqItems.length, node: <ReqList list={cur.reqItems} /> };
      case 'cond': return txt(condText(cur.cond));
      case 'amount': return txt(amountNow(cur));
      case 'canon': return txt(cur.canon);
      default: return txt('');
    }
  }
  if (!ai) return { empty: true, node: <Dash /> };
  switch (k) {
    case 'item': return txt(ai.item);
    case 'biz': return { empty: !ai.biz.length, node: <Chips items={ai.biz} tone="ai" /> };
    case 'subs': {
      const extra = ai.any.filter(g => g.length > 1).map(g => `또는(${g.join(' / ')})`);
      const all = ai.subs.concat(extra);
      return { empty: !all.length, node: <Chips items={all} tone="ai" /> };
    }
    case 'tech': return txt(ai.tech ? '켬' : '');
    case 'bizx': return { empty: !ai.bizx.length, node: <Chips items={ai.bizx} tone="ai" /> };
    case 'sec': return { empty: !ai.sec.length, node: <Chips items={ai.sec} tone="ai" /> };
    case 'req': return { empty: !ai.req.length, node: <ReqList list={ai.req} /> };
    case 'cond': return txt(condText(ai.cond));
    case 'amount': return txt(amountAi(ai));
    case 'canon': return txt(ai.canon);
    default: return txt('');
  }
}

/** 공고 종류(AI kind) — backend govTargetsAi.KINDS */
const KIND_AI_LABEL: Record<string, string> = { agency_recruit: '기관 모집', umbrella: '통합 공고', amendment: '개정 공고' };
const trackText = (t: GovCheckAiTrack) => [t.category, t.form === 'consult' ? '컨설팅만' : '', t.amountMax != null ? `최대 ${t.amountMax.toLocaleString()}만원` : '', ...(t.biz || []), ...(t.subs || [])].filter(Boolean).join(' · ');
/** AI 가 읽은 지역 · 지원형태 · 종류 · 정책자금 · 세부 유형 한 줄 (2026-10-08) — 값이 하나도 없으면 안 그린다 · 규칙 검사 region 이 걸리면 「지역 없음」을 확인 색으로 */
function MetaLine({ ai, flags }: { ai: GovCheckAi; flags: GovCheckFlag[] }) {
  const regionFlag = flags.find(f => f.k === 'region');
  const tracks = ai.tracks || [];
  const parts: { k: string; label: string; text: string; warn?: boolean }[] = [];
  if (ai.region) parts.push({ k: 'region', label: '지역', text: ai.region });
  else if (regionFlag) parts.push({ k: 'region', label: '지역', text: '없음', warn: true });
  if (ai.form === 'consult') parts.push({ k: 'form', label: '지원형태', text: '컨설팅만' });
  if (ai.kind) parts.push({ k: 'kind', label: '종류', text: KIND_AI_LABEL[ai.kind] || ai.kind });
  if (ai.loan && (ai.loan.type || ai.loan.use)) parts.push({ k: 'loan', label: '정책자금', text: [ai.loan.type, ai.loan.use].filter(Boolean).join(' · ') });
  if (!parts.length && !tracks.length) return null;
  return (
    <div data-part="meta" data-region={ai.region || ''} data-form={ai.form || ''} data-kind={ai.kind || ''} data-tracks={tracks.length}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 14px', margin: '0 14px 10px', padding: '6px 10px', borderRadius: R.sm, background: C.bgAlt, border: `1px solid ${regionFlag && !ai.region ? AMBER.line : C.border}`, fontSize: T.sm, color: C.text, lineHeight: 1.6 }}>
      <span style={{ fontSize: T.xs, fontWeight: 700, color: C.muted }}>AI</span>
      {parts.map(p => (
        <span key={p.k} data-meta={p.k} style={{ color: p.warn ? AMBER.text : C.text, fontWeight: p.warn ? 700 : 400 }}>
          <b style={{ color: p.warn ? AMBER.text : C.sub }}>{p.label}</b> {p.text}
        </span>
      ))}
      {tracks.length > 0 && (
        <span data-meta="tracks" style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 4 }}>
          <b style={{ color: C.sub, marginRight: 2 }}>세부유형</b>
          {tracks.map((t, i) => (
            <span key={`${t.name}-${i}`} style={{ padding: '1px 7px', borderRadius: 5, fontSize: T.sm, lineHeight: 1.6, background: C.white, border: `1px solid ${C.border}`, color: C.text, overflowWrap: 'anywhere' }}>
              {t.name}
              {t.isNew && <span style={{ fontSize: T.xs, fontWeight: 700, color: C.accent, marginLeft: 4 }}>새로</span>}
              {trackText(t) && <span style={{ color: C.sub }}> · {trackText(t)}</span>}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

const CheckCard = React.memo(function CheckCard({ it, kind, diff, mark, compact }: { it: GovCheckItem; kind: GovCheckKind; diff: FieldKey[]; mark: GoldMark | null; compact: boolean }) {
  const [openEv, setOpenEv] = useState(false);
  const ai = it.ai, cur = it.current;
  const m = mark ? MARK[mark] : null;
  const conf = ai ? ai.confidence : null;
  const fieldRows = FIELDS.map(f => ({ ...f, now: cellOf(f.k, cur, ai, 'now'), ai: cellOf(f.k, cur, ai, 'ai'), diff: diff.includes(f.k) }));
  const visible = fieldRows.filter(f => f.diff || !f.now.empty || !f.ai.empty);
  const bothEmpty = fieldRows.filter(f => !f.diff && f.now.empty && f.ai.empty);
  const evid = ai ? ai.evidence.concat(ai.req.filter(q => q.ev).map(q => ({ k: `자격 · ${q.t}`, q: q.ev || '' }))) : [];
  const border = mark === 'fail' || mark === 'error' ? '#FECACA' : mark === 'soft' ? AMBER.line : C.border;
  const corpus = kind === 'corpus';
  const aiOnly = corpus || (!cur && !!it.caseCorpus && !it.missing);   // 수집 공고 — 「지금」 열이 없어 원문(지원대상) | AI 로 (시험 세트의 수집 사례도)
  const flags = flagsOf(it);
  const level = flagLevel(it);
  const mLabel = mark ? markLabel(kind, mark) : '';
  return (
    <article data-check-row={it.n} data-program-id={it.id || ''} data-kind={kind} data-gold={mark || ''} data-confidence={conf || ''}
      data-dropped={ai ? ai.dropped.length : 0} data-diff={diff.join(',')}
      {...(corpus ? { 'data-flags': Array.from(new Set(flags.map(f => f.k))).join(','), 'data-flag-level': ai && !it.err ? level : 'error' } : {})}
      {...(it.caseCorpus ? { 'data-corpus-id': it.caseCorpus } : {})}
      style={{ border: `1px solid ${border}`, borderRadius: R.md, background: C.white, overflow: 'hidden' }}>
      {/* 머리 */}
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 14px', background: m && mark !== 'ok' ? m.bg : C.white }}>
        {m && (
          <span aria-label={mLabel} title={mLabel} style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: T.md, fontWeight: 800, color: m.color, background: mark === 'ok' ? m.bg : C.white, border: `1px solid ${m.color}33` }}>{m.ch}</span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.sm, fontWeight: 700, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{it.n}</span>
            {it.link
              ? <a data-part="title" href={it.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: T.md, fontWeight: 700, color: C.text, textDecoration: 'none', borderBottom: `1px solid ${C.borderDk}`, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{it.name}</a>
              : <span data-part="title" style={{ fontSize: T.md, fontWeight: 700, color: C.text, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{it.name}</span>}
          </div>
          {(corpus || it.caseCorpus) && it.id && <div style={{ fontSize: T.xs, color: C.muted, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>수집 id {it.id}{it.source ? ` · ${it.source}` : ''}</div>}
          {kind === 'gold' && it.caseName && it.caseName !== it.name && <div style={{ fontSize: T.sm, color: C.sub, marginTop: 2 }}>시험 사례 「{it.caseName}」</div>}
          <div style={{ fontSize: T.sm, color: C.sub, marginTop: 3, lineHeight: 1.5 }}>
            {[it.agency, it.deadline ? `마감 ${it.deadline}` : it.firstCome ? '상시' : '', it.briefName ? `리포트명 「${it.briefName}」` : '', it.bodyChars != null ? `본문 ${it.bodyChars.toLocaleString()}자` : ''].filter(Boolean).join(' · ')
              || (it.missing ? (it.caseCorpus ? `수집 공고 파일(scripts/gov-check-corpus.json)에서 ${it.caseCorpus} 를 찾지 못했습니다` : '등록된 공고에서 찾지 못했습니다(지워짐)') : '')}
          </div>
        </div>
        {conf && <Badge bg={CONF_COLOR[conf][0]} color={CONF_COLOR[conf][1]} style={{ flexShrink: 0 }}>확신 {CONF_LABEL[conf]}</Badge>}
      </header>

      {kind === 'gold' && it.bad && it.bad.length > 0 && (
        <div data-part="bad" style={{ padding: '8px 14px', borderTop: `1px solid ${border}`, background: mark === 'soft' ? AMBER.bg : C.redLt, color: mark === 'soft' ? AMBER.text : '#991B1B', fontSize: T.sm, lineHeight: 1.55 }}>
          <b>기대와 다름{mark === 'soft' ? ' (참고 — 막지 않음)' : ''}</b>
          <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>{it.bad.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
      {corpus && ai && !it.err && flags.length > 0 && (
        <div data-part="flags" style={{ padding: '8px 14px', borderTop: `1px solid ${border}`, background: level === 'fail' ? C.redLt : AMBER.bg, fontSize: T.sm, lineHeight: 1.55 }}>
          {(['fail', 'warn'] as const).map(lv => {
            const list = flags.filter(f => f.level === lv);
            if (!list.length) return null;
            return (
              <div key={lv} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: lv === 'warn' && level === 'fail' ? 4 : 0 }}>
                <b style={{ flexShrink: 0, width: 30, color: lv === 'fail' ? '#991B1B' : AMBER.text }}>{lv === 'fail' ? '틀림' : '확인'}</b>
                <ul style={{ margin: 0, paddingLeft: 16, minWidth: 0, color: lv === 'fail' ? '#991B1B' : AMBER.text }}>
                  {list.map((f, i) => <li key={`${f.k}-${i}`} data-flag={f.k} data-level={f.level} style={{ overflowWrap: 'anywhere' }}>{f.t}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {(it.err || (!ai && !it.missing)) && (
        <div data-part="error" style={{ padding: '8px 14px', borderTop: `1px solid ${border}`, background: C.redLt, color: '#991B1B', fontSize: T.sm }}>
          AI 읽기 실패 — {it.err || '답 없음'}
        </div>
      )}

      {/* 수집 공고(시험 세트의 수집 사례도): 원문(지원대상) | AI */}
      {aiOnly && <CorpusBody it={it} ai={ai} flags={flags} compact={compact} />}

      {/* 지금 ↔ AI */}
      {cur && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: compact ? '6px 12px 10px' : '6px 14px 10px' }}>
          {!compact && (
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10, padding: '4px 0 6px', fontSize: T.xs, fontWeight: 700, color: C.muted }}>
              <span>항목</span><span>지금</span><span>AI</span>
            </div>
          )}
          {visible.map(f => (
            <div key={f.k} data-field={f.k} data-diff={f.diff ? '1' : '0'}
              style={{ display: compact ? 'flex' : 'grid', flexDirection: 'column', gridTemplateColumns: compact ? undefined : GRID, gap: compact ? 3 : 10, alignItems: 'start',
                padding: compact ? '7px 0 7px 8px' : '6px 0 6px 8px', borderTop: `1px solid ${C.bg}`, fontSize: T.sm, lineHeight: 1.6, background: f.diff ? '#F8FAFF' : 'transparent' }}>
              <span style={{ fontWeight: 700, color: f.diff ? C.accent : C.sub }}>{f.label}{f.diff && <span style={{ fontSize: T.xs, fontWeight: 600, marginLeft: 6 }}>다름</span>}</span>
              <span data-side="now" style={{ color: C.text, minWidth: 0, overflowWrap: 'anywhere' }}>{compact && <SideTag t="지금" />}{f.now.node}</span>
              <span data-side="ai" style={{ color: C.text, minWidth: 0, overflowWrap: 'anywhere', fontWeight: f.diff ? 700 : 400 }}>{compact && <SideTag t="AI" />}{f.ai.node}</span>
            </div>
          ))}
          {bothEmpty.length > 0 && (
            <div data-part="both-empty" style={{ padding: '6px 0 0 8px', fontSize: T.xs, color: C.muted, borderTop: `1px solid ${C.bg}` }}>
              둘 다 없음 — {bothEmpty.map(f => f.label).join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* AI 가 읽은 지역 · 지원형태 · 종류 · 정책자금 · 세부 유형 — 「지금」 열이 없는 값이라 표 아래 한 줄로 */}
      {ai && <MetaLine ai={ai} flags={flags} />}

      {ai && ai.dropped.length > 0 && (
        <div data-part="dropped" style={{ margin: '0 14px 10px', padding: '7px 10px', borderRadius: R.sm, background: AMBER.bg, border: `1px solid ${AMBER.line}`, color: AMBER.text, fontSize: T.sm, lineHeight: 1.55 }}>
          <b>버린 값</b> (목록에 없는 이름) — {ai.dropped.join(' · ')}
        </div>
      )}
      {ai && (ai.note || evid.length > 0) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '0 14px 11px', fontSize: T.sm, color: C.sub, lineHeight: 1.55 }}>
          {ai.note && <span data-part="note" style={{ flex: 1, minWidth: 0 }}><b style={{ color: C.text }}>메모</b> {ai.note}</span>}
          {!ai.note && <span style={{ flex: 1 }} />}
          {evid.length > 0 && (
            <button type="button" onClick={() => setOpenEv(v => !v)} aria-expanded={openEv}
              style={{ flexShrink: 0, border: 'none', background: 'transparent', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              근거 {evid.length} {openEv ? '접기' : '보기'}
            </button>
          )}
        </div>
      )}
      {ai && openEv && evid.length > 0 && (
        <div data-part="evidence" style={{ margin: '0 14px 12px', padding: '8px 10px', borderRadius: R.sm, background: C.bgAlt, border: `1px solid ${C.border}`, fontSize: T.sm, lineHeight: 1.6 }}>
          {evid.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8 }}>
              <span style={{ flexShrink: 0, fontWeight: 700, color: C.sub, maxWidth: '40%', overflowWrap: 'anywhere' }}>{e.k}</span>
              <span style={{ color: C.text, minWidth: 0, overflowWrap: 'anywhere' }}>「{e.q}」</span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
});
const GRID = '92px minmax(0, 1fr) minmax(0, 1fr)';
const GRID_AI = '84px minmax(0, 1fr)';
/** 규칙 검사 k → 표시할 칸 (dropped · conf · none 은 칸이 아니라 카드 전체) */
const FLAG_FIELD: Partial<Record<string, FieldKey>> = { req: 'req', biz: 'biz', item: 'item', amount: 'amount', subs: 'subs' };

/** 수집 공고 카드 몸통 — 왼쪽 원문(지원대상) 발췌 · 오른쪽 AI 칸 (지금 등록값이 없으니 「지금」 열 대신) */
function CorpusBody({ it, ai, flags, compact }: { it: GovCheckItem; ai: GovCheckAi | null; flags: GovCheckFlag[]; compact: boolean }) {
  const lv: Partial<Record<FieldKey, 'fail' | 'warn'>> = {};
  for (const f of flags) { const k = FLAG_FIELD[f.k]; if (k && lv[k] !== 'fail') lv[k] = f.level; }
  const cells = FIELDS.map(f => ({ ...f, ai: cellOf(f.k, null, ai, 'ai'), lv: lv[f.k] }));
  const shownCells = cells.filter(f => f.lv || !f.ai.empty);
  const emptyCells = cells.filter(f => !f.lv && f.ai.empty);
  const target = (it.target || '').trim();
  const barOf = (l?: 'fail' | 'warn') => (l === 'fail' ? C.red : l === 'warn' ? '#D97706' : 'transparent');
  return (
    <div style={{ borderTop: `1px solid ${C.border}`, display: compact || !ai ? 'block' : 'grid', gridTemplateColumns: compact || !ai ? undefined : 'minmax(0, 5fr) minmax(0, 6fr)' }}>
      <section data-part="target" style={{ padding: compact ? '10px 12px' : '10px 14px 12px', borderRight: compact || !ai ? 'none' : `1px solid ${C.border}`, borderBottom: compact && ai ? `1px solid ${C.border}` : 'none', background: C.bgAlt, minWidth: 0 }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.muted, marginBottom: 4 }}>원문(지원대상)</div>
        {target
          ? <div style={{ fontSize: T.sm, color: C.text, lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: compact ? 220 : 300, overflowY: 'auto' }}>{target}</div>
          : <div style={{ fontSize: T.sm, color: C.muted }}>지원대상 문단을 찾지 못했습니다</div>}
      </section>
      {ai && (
        <section style={{ padding: compact ? '4px 12px 10px' : '4px 14px 10px', minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: GRID_AI, gap: 10, padding: '6px 0', fontSize: T.xs, fontWeight: 700, color: C.muted }}>
            <span>항목</span><span>AI</span>
          </div>
          {shownCells.map(f => (
            <div key={f.k} data-field={f.k} data-diff="0" data-flag-level={f.lv || ''}
              style={{ display: 'grid', gridTemplateColumns: GRID_AI, gap: 10, alignItems: 'start', padding: '6px 0 6px 8px', borderTop: `1px solid ${C.bg}`, fontSize: T.sm, lineHeight: 1.6, background: f.lv === 'fail' ? '#FFF7F7' : f.lv === 'warn' ? '#FFFDF5' : 'transparent' }}>
              <span style={{ fontWeight: 700, color: f.lv === 'fail' ? C.red : f.lv === 'warn' ? AMBER.text : C.sub }}>{f.label}</span>
              <span data-side="ai" style={{ color: C.text, minWidth: 0, overflowWrap: 'anywhere' }}>{f.ai.node}</span>
            </div>
          ))}
          {emptyCells.length > 0 && (
            <div data-part="both-empty" style={{ padding: '6px 0 0 8px', fontSize: T.xs, color: C.muted, borderTop: `1px solid ${C.bg}` }}>
              AI 답 없음 — {emptyCells.map(f => f.label).join(' · ')}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
function SideTag({ t }: { t: string }) {
  return <span style={{ display: 'inline-block', width: 30, fontSize: T.xs, fontWeight: 700, color: C.muted }}>{t}</span>;
}
